/**
 * Veroni "More details ↓" smooth-scroll — fix #3.
 *
 * The PDP's scroll container is `.page-wrapper` (Horizon's app-shell), not
 * `window`/`html`/`body`: at >=990px, base.css sets `html { overflow: hidden }`
 * and `.page-wrapper { height: 100dvh; overflow-y: auto; scroll-behavior: smooth }`.
 * A plain `<a href="#more-details">` triggers the browser's native
 * fragment-scroll on the window/document, which is a no-op here since the
 * window itself never scrolls — the anchor looked "broken" (no visible
 * movement) even though the target section render was correct.
 *
 * This intercepts clicks on `.v-more-details-link` and scrolls
 * `.page-wrapper` itself to the target section's offset instead.
 * `.page-wrapper` already has `scroll-behavior: smooth` (base.css), so a
 * plain `scrollTop` assignment animates smoothly; `scrollTo({ behavior:
 * 'smooth' })` is used explicitly anyway for robustness in case that CSS
 * property is ever removed upstream.
 */
(function () {
  function scrollWrapperTo(target) {
    var wrapper = document.querySelector('.page-wrapper');
    if (!wrapper || !target) return false;

    // `.page-wrapper` is only the scroll container at >=990px — base.css scopes
    // `html { overflow: hidden }` and the wrapper's own `height: 100dvh;
    // overflow-y: auto` to that breakpoint. Below it the document scrolls and
    // this element does not, so wrapper.scrollTo() is a no-op. Returning true
    // anyway meant the caller ran preventDefault() and suppressed the native
    // fragment jump, which killed EVERY in-page anchor on tablet and mobile,
    // "More details ↓" included. Hand those back to the browser.
    if (wrapper.scrollHeight <= wrapper.clientHeight) return false;

    // Sticky header sits above the scroll container's content; offset by
    // its live height (already tracked as --header-group-height on body)
    // so the section heading isn't hidden behind it.
    var headerOffset = parseFloat(getComputedStyle(document.body).getPropertyValue('--header-group-height')) || 0;

    var wrapperRect = wrapper.getBoundingClientRect();
    var targetRect = target.getBoundingClientRect();
    var currentScroll = wrapper.scrollTop;
    var destination = currentScroll + (targetRect.top - wrapperRect.top) - headerOffset - 16;

    var dest = Math.max(destination, 0);
    // A JS `behavior: 'smooth'` argument beats the `scroll-behavior: auto
    // !important` reduced-motion rule in veroni-theme.css, so honour the
    // preference here rather than overriding it.
    var reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    wrapper.scrollTo({ top: dest, behavior: reduced ? 'auto' : 'smooth' });
    // resilience: if smooth scrolling is stalled (throttled tab, quirky env),
    // fall back to an instant jump so the anchor always works.
    setTimeout(function () {
      if (Math.abs(wrapper.scrollTop - currentScroll) < 40 && Math.abs(dest - currentScroll) >= 40) {
        var prev = wrapper.style.scrollBehavior;
        wrapper.style.scrollBehavior = 'auto';
        wrapper.scrollTop = dest;
        wrapper.style.scrollBehavior = prev;
      }
    }, 600);
    return true;
  }

  document.addEventListener('click', function (event) {
    var link = event.target.closest && event.target.closest('a[href^="#"]');
    if (!link) return;

    // Never intercept the skip-to-content link: it must move keyboard focus
    // synchronously via native fragment navigation.
    if (link.classList.contains('skip-to-content-link')) return;

    var hash = link.getAttribute('href');
    if (!hash || hash.length < 2) return;

    var target = document.getElementById(hash.slice(1));
    if (!target) return;

    // Only intercept in-page anchors whose target actually exists on the
    // page — anything else falls through to default browser behavior.
    if (scrollWrapperTo(target)) {
      event.preventDefault();
      if (history.pushState) {
        history.pushState(null, '', hash);
      }
      // Native fragment navigation moves focus to the target; mirror that
      // so keyboard/AT users land where the page scrolled.
      if (!target.hasAttribute('tabindex')) {
        target.setAttribute('tabindex', '-1');
      }
      target.focus({ preventScroll: true });
    }
  });
})();
